import { Injectable, signal } from "@angular/core";
import { FilterChangesInterface } from "@kernel/listing/interfaces/filters.interface";
import { TControlsValue } from "@kernel/form/types/form-controls-utils.types";
import { IPropertyFiltersControls } from "@app/core/filtering/interfaces/property-filters-controls.interface";
import { SearchChangesInterface } from "@kernel/listing/interfaces/search.interface";

@Injectable()
export class PropertyFiltersService {
  /**
   * Reactive signal holding the currently applied filters.
   */
  appliedFilters = signal<FilterChangesInterface | undefined>(undefined);

  /**
   * Reactive signal containing preview ready values for the applied filters.
   */
  appliedFiltersPreview = signal<Record<string, string> | undefined>(undefined);

  /**
   * Reactive signal containing a snapshot of the form control values
   * at the moment the filters are applied.
   */
  rawAppliedFilters = signal<TControlsValue<IPropertyFiltersControls> | undefined>(undefined);

  /**
   * Reactive signal representing the currently applied search criteria.
   */
  appliedSearch = signal<SearchChangesInterface | undefined>(undefined);

  /**
   * Applies the given filter values to the reactive signals.
   *
   * @param appliedFilters - The filters that were applied (snapshot of filter conditions and values).
   * @param appliedFiltersPreview - A preview of filters, typically used for showing pending or intermediate filter changes.
   * @param rawAppliedFilters - The raw form control values at the time the filters were applied.
   */
  applyFilters(
    appliedFilters?: FilterChangesInterface,
    appliedFiltersPreview?: Record<string, string>,
    rawAppliedFilters?: TControlsValue<IPropertyFiltersControls>
  ) {
    this.appliedFilters.set(appliedFilters);
    this.rawAppliedFilters.set(rawAppliedFilters);
    this.appliedFiltersPreview.set(appliedFiltersPreview);
  }

  /**
   * Applies the given search values to the reactive signal.
   *
   * @param search - The search changes to apply, or `undefined` to clear the search.
   */
  applySearch(search: SearchChangesInterface | undefined) {
    this.appliedSearch.set(search);
  }
}
